<template>
  <div id="member-dashboard">
    <!-- HEADER -->
    <section class="pt-lg-5 py-4">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <section-header
              title="Member Dashboard"
              :subtitle="'Welcome back ' + user.fname"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- CREDITS/IMAGES WIDGETS -->
    <section>
      <div class="container">
        <div class="row">
          <div
            v-if="user.exclusiveCreditCount > 0"
            :class="user.exclusiveCreditCount > 0 ? 'col-12 col-lg-6' : ''"
          >
            <card class="widget">
              <div>
                <div class="icon-circle bg-gradient-success">
                  <i class="fas fa-coins"></i>
                </div>
              </div>
              <div class="widget-text">
                <div class="widget-subheading">CREDITS REMAINING</div>
                <div class="widget-heading">
                  CREDITS: {{ user.exclusiveCreditCount }}
                </div>
              </div>
              <div class="widget-cta d-none d-md-block">
                <el-tooltip
                  effect="dark"
                  placement="top"
                  content="Art Grab is no longer using a credits system. Any remaining credits will be automatically applied when purchasing images until they have all been used."
                >
                  <i class="fas fa-exclamation-triangle" />
                </el-tooltip>
              </div>
              <div
                class="widget-cta d-flex d-md-none widget-alert align-items-center"
              >
                <div class="d-flex justify-items-center align-items-center">
                  <i class="fas fa-exclamation-triangle mr-4" />
                </div>
                <div class="d-flex justify-items-center align-items-center">
                  <small
                    >Art Grab is no longer using a credits system. Any remaining
                    credits will be automatically applied when purchasing images
                    until they have all been used.</small
                  >
                </div>
              </div>
            </card>
          </div>
          <div
            :class="
              user.exclusiveCreditCount > 0 ? 'col-12 col-lg-6' : 'col-12'
            "
          >
            <card class="widget">
              <div>
                <div class="icon-circle bg-gradient-info">
                  <i class="far fa-images"></i>
                </div>
              </div>
              <div class="widget-text">
                <div class="widget-subheading">CLAIMED ARTWORK</div>
                <div
                  v-if="user.claimedAnimations.length === 0"
                  class="widget-heading"
                >
                  IMAGES: {{ user.claimedImages.length }}
                </div>
                <div v-else class="widget-heading d-flex icon-wrapper-artwork">
                  <div class="icon-count-wrapper">
                    <i class="fas fa-image"></i>
                    {{ user.claimedImages.length }}
                  </div>
                  <div class="icon-count-wrapper ml-4">
                    <i class="fas fa-play-circle"></i>
                    {{ user.claimedAnimations.length }}
                  </div>
                </div>
              </div>
              <div class="widget-cta">
                <router-link class="btn btn-primary" to="/myimages"
                  >MY ARTWORK</router-link
                >
              </div>
            </card>
          </div>
        </div>
      </div>
    </section>

    <!-- RECENTLY VIEWED -->
    <section class="my-lg-3 my-2">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div class="dashboard-section-heading mb-lg-4 mb-3">
              <h3>RECENTLY VIEWED</h3>
            </div>
            <image-slider
              v-if="recentlyViewed.length > 0"
              :images="recentlyViewed"
              sliderRef="recentlyViewedSlider"
            />
            <div v-else class="not-found-container">
              You haven't viewed any images recently.
              <router-link
                to="/store"
                @click.native="resetSearch"
                class="btn btn-sm btn-outline-primary mt-3"
                >BROWSE AVAILABLE IMAGES</router-link
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- COLLECTIONS -->
    <section class="mt-5 my-2">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div
              class="dashboard-section-heading mb-lg-4 mb-3 d-flex justify-content-between align-items-center pb-1"
            >
              <h3>COLLECTIONS</h3>
              <router-link to="/mycollections" class="btn btn-sm btn-primary"
                >MY COLLECTIONS
                {{
                  user.collections.length > 3
                    ? "(" + user.collections.length + ")"
                    : ""
                }}</router-link
              >
            </div>
            <div
              v-if="user.collections.length === 0"
              class="not-found-container"
            >
              You haven't created any collections yet.
              <base-button
                @click="handleCreateCollection"
                type="outline-primary"
                class="mt-3"
                size="sm"
                >CREATE COLLECTION</base-button
              >
            </div>
            <div v-else class="row collection-wrapper">
              <div
                class="card-wrapper col-md-4 mb-3"
                v-for="collection in user.collections.slice(0, 3)"
                v-bind:key="'collection--' + collection.id"
              >
                <router-link
                  :to="'/user/' + user.id + '/collection/' + collection.id"
                >
                  <div
                    class="collection-card"
                    :style="getCollectionCardBackground(collection)"
                  >
                    <div class="collection-card-overlay">
                      <div class="collection-name">{{ collection.name }}</div>
                      <div>
                        <div class="collection-card-count bg-gradient-info">
                          {{
                            collection.photos.length +
                            collection.animations.length
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FOLLOWING -->
    <section class="mt-5 my-2">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div
              class="dashboard-section-heading mb-lg-4 mb-3 d-flex justify-content-between align-items-center pb-1"
            >
              <h3>FOLLOWING</h3>
              <base-button
                @click="modals.followingModal = true"
                type="primary"
                size="sm"
                v-if="user.following.length > 0"
                >MANAGE</base-button
              >
            </div>
            <div v-if="user.following.length === 0" class="not-found-container">
              You haven't followed any artists yet.
              <base-button
                @click="$router.push({ path: '/artists' })"
                type="outline-primary"
                class="mt-3"
                size="sm"
                >FIND ARTISTS TO FOLLOW</base-button
              >
            </div>
            <div v-else class="row following-wrapper">
              <div
                class="col-3 col-md-1 mb-3"
                v-for="artist in user.following.slice(
                  0,
                  user.following.length === 12 ? 12 : 11
                )"
                v-bind:key="'following--' + artist.artist_id"
              >
                <router-link :to="'/art/' + artist.handle.replace('@', '')">
                  <img
                    :src="artist.profile_image"
                    class="following-badge rounded-circle"
                    :alt="artist.handle + ' avatar'"
                  />
                </router-link>
              </div>
              <div
                class="col-3 col-md-1 mb-3"
                v-if="user.following.length > 12"
              >
                <button
                  class="following-more-badge"
                  @click="modals.followingModal = true"
                >
                  <div class="following-more-count rounded-circle">
                    +{{ this.user.following.length - 11 }}
                  </div>
                </button>
              </div>
            </div>
            <div v-if="user.following.length > 0" class="row">
              <div class="col-12">
                <div class="latest-link text-center">
                  View the newest artwork from artists you follow on the
                  <router-link to="/latest">Latest</router-link> page.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ACCOUNT WIDGETS -->
    <section class="my-lg-3 mb-lg-5 my-2">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div class="dashboard-section-heading mt-4 mb-lg-4 mb-3">
              <h3>MY ACCOUNT</h3>
            </div>
            <div class="row">
              <div class="col-md-6">
                <account-widget
                  name="paymentOption"
                  title="PAYMENT OPTION"
                  buttonText="EDIT PAYMENT OPTION"
                  @paymentOptionClicked="handlePaymentOptionClicked"
                >
                  <div
                    v-if="hasPaymentOption"
                    class="d-flex justify-content-around"
                  >
                    <i
                      v-if="user.card.brand === 'Visa'"
                      class="fab cc-icon fa-cc-visa"
                    ></i>
                    <i
                      v-else-if="user.card.brand === 'MasterCard'"
                      class="fab cc-icon fa-cc-mastercard"
                    ></i>
                    <i
                      v-else-if="user.card.brand === 'Discover'"
                      class="fab cc-icon fa-cc-discover"
                    ></i>
                    <i
                      v-else-if="user.card.brand === 'American Express'"
                      class="fab cc-icon fa-cc-amex"
                    ></i>
                    <strong v-else>{{ user.card.brand }}</strong>
                    <div>*******{{ user.card.last_four }}</div>
                    <div v-if="isCardExpired" class="text-danger">
                      <el-tooltip
                        effect="dark"
                        placement="top"
                        content="Card may be expired. Please update payment option."
                      >
                        <div class="warning-badge">
                          <i class="fas fa-exclamation-triangle mr-1"></i>EXP:
                          {{ user.card.exp_month }}/{{ user.card.exp_year }}
                        </div>
                      </el-tooltip>
                    </div>
                    <div v-else>
                      EXP: {{ user.card.exp_month }}/{{ user.card.exp_year }}
                    </div>
                  </div>
                  <div v-else>No payment option selected.</div>
                </account-widget>
              </div>
              <div class="col-md-6">
                <account-widget
                  name="paymentHistory"
                  title="PAYMENT HISTORY"
                  buttonText="FULL PAYMENT HISTORY"
                  @paymentHistoryClicked="handlePaymentHistoryClicked"
                >
                  <div
                    v-if="user.invoices.length > 0"
                    class="d-flex justify-content-around"
                  >
                    <div>{{ user.invoices[0].status }}</div>
                    <div>{{ user.invoices[0].created }}</div>
                    <div>{{ user.invoices[0].amount_paid }}</div>
                  </div>
                  <div v-else>No invoices found.</div>
                </account-widget>
              </div>
              <div class="col-md-6">
                <account-widget
                  name="emailPreferences"
                  title="EMAIL PREFERENCES"
                  buttonText="UPDATE PREFERENCES"
                  @emailPreferencesClicked="handleEmailPreferencesClicked"
                ></account-widget>
              </div>
              <div class="col-md-6">
                <account-widget
                  name="changePassword"
                  title="PASSWORD"
                  buttonText="CHANGE PASSWORD"
                  @changePasswordClicked="handleChangePasswordClicked"
                ></account-widget>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- MODALS -->
    <update-payment-option />
    <payment-history />
    <change-password />
    <following-modal />
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  ImageSlider,
  SectionHeader,
  AccountWidget,
  UpdatePaymentOption,
  PaymentHistory,
  ChangePassword,
  FollowingModal,
} from "../../components";

export default {
  name: "member-dashboard",
  metaInfo: {
    title: "Member Dashboard - Art Grab",
  },
  data() {
    return {
      card: { brand: "", last_four: "", exp_year: "", exp_month: "" },
      modals: {
        paymentOption: false,
        paymentHistory: false,
        changePassword: false,
        followingModal: false,
      },
    };
  },
  components: {
    ImageSlider,
    SectionHeader,
    AccountWidget,
    UpdatePaymentOption,
    PaymentHistory,
    ChangePassword,
    FollowingModal,
  },
  computed: {
    ...mapState([
      "recentlyViewed",
      "user",
      "hasPaymentOption",
      "claimedImages",
      "stripe",
    ]),
    isCardExpired() {
      const cardDate = new Date(
        `${this.user.card.exp_month}/28/${this.user.card.exp_year}`
      );
      const today = new Date();
      return cardDate < today;
    },
  },
  methods: {
    handleCheckout() {},
    handleEmailPreferencesClicked() {
      this.$router.push({ path: "/emailpreferences" });
    },
    handleCreateCollection() {
      this.$store.commit("showCreateCollectionModal", true);
    },
    resetSearch() {
      this.$store.commit("setSearchPage", 1);
    },
    handlePaymentOptionClicked() {
      this.modals.paymentOption = true;
    },
    handlePaymentHistoryClicked() {
      this.modals.paymentHistory = true;
    },
    handleChangePasswordClicked() {
      this.modals.changePassword = true;
    },
    getCollectionCardBackground(collection) {
      if (collection && collection.photos.length > 0) {
        return `background-image: url('${collection.photos[0].url_path}')`;
      } else if (collection && collection.animations.length > 0) {
        return `background-image: url('${collection.animations[0].poster_url_path}')`;
      }
      return `background: black`;
    },
  },
  mounted() {
    this.$store.commit("setLayout", "page-layout");
    this.$store.dispatch("getClaimedImages");
    this.$store.dispatch("getPaymentOption");
    this.$store.dispatch("getPaymentHistory");
    this.$store.dispatch("getCollections");
    this.$store.dispatch("getCreditInfo");
  },
};
</script>

<style lang="scss">
#member-dashboard {
  padding-top: 100px;

  .dashboard-section-heading {
    border-bottom: 1px solid #000;
  }

  .cc-icon {
    font-size: 1.25rem;
    color: #666;
  }

  .latest-link {
    background-color: #f7f7f7;
    padding: 6px 15px;
    border-radius: 3px;
  }

  .collection-wrapper {
    .collection-card {
      border-radius: 3px;
      background-size: cover;
      background-position: center;

      .collection-card-overlay {
        padding: 36px 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: rgba(0, 0, 0, 0.5);
        border-radius: 3px;

        .collection-name {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          font-weight: 600;
          color: white;
          margin-bottom: 0;
        }

        .collection-card-count {
          font-size: 0.75rem;
          text-transform: uppercase;
          padding: 5px 12px;
          border-radius: 25px;
          width: 25px;
          height: 25px;
          display: flex;
          justify-content: center;
          align-items: center;
          color: white;
        }
      }
    }
  }

  .following-badge {
    width: 50px;
    height: 50px;
  }

  .following-more-badge {
    padding: 0;
    outline: none;
    border: none;
    background: transparent;

    .following-more-count {
      width: 50px;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #cccccc;
      color: white;
    }
  }

  .not-found-container {
    padding: 1.75rem 1rem;
    background: #f7f7f7;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .widget {
    .card-body {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    .widget-text {
      padding-left: 1.5rem;
      flex: 1;

      .widget-subheading {
        font-size: 0.875rem;
        color: #adadad;
        font-weight: 600;
      }

      .widget-heading {
        font-size: 1.75rem;
        line-height: 1.2;
        font-weight: 700;

        &.icon-wrapper-sales {
          justify-content: flex-end;
        }

        .icon-count-wrapper {
          i {
            color: #888;
          }
        }
      }
    }

    .icon-circle {
      width: 50px;
      height: 50px;
      border-radius: 25px;
      display: flex;
      justify-content: center;
      align-items: center;

      i {
        color: white;
        font-size: 1.5rem;
      }
    }

    .fa-exclamation-triangle {
      font-size: 36px;
      color: red;
    }
  }
}

@media (max-width: 991.98px) {
  #member-dashboard {
    padding-top: 100px;
  }
}

@media (max-width: 575.98px) {
  #member-dashboard {
    .widget {
      .card-body {
        flex-wrap: wrap;

        .widget-heading {
          font-size: 1.25rem;

          &.icon-wrapper-artwork {
            justify-content: flex-end;
          }
        }

        .icon-circle {
          width: 50px;
          height: 50px;

          i {
            font-size: 1.25rem;
          }
        }

        .widget-text {
          text-align: right;
        }

        .widget-cta {
          margin-top: 1rem;
          width: 100%;

          &.widget-alert {
            color: red;
            font-size: 12px;

            .fa-exclamation-triangle {
              font-size: 16px;
              color: red;
            }
          }

          a {
            display: block;
          }
        }
      }
    }
  }
}
</style>
